"use client";

import { usePathname } from "next/navigation";
import { whatsappNumber } from "@/lib/site";
import { usePublicSettings } from "@/lib/usePublicSettings";

// wa.me pre-fills the message box but still requires the visitor to hit send -- that send is
// a real inbound message to the same WhatsApp Business number the bot answers on
// (functions/src/webhooks/whatsapp.ts), so it triggers the same automated reply/FAQ flow as
// texting the number directly.
const PREFILLED_MESSAGE = "Hi! I'd like to know more about Hoop & Yoga Fitness sessions.";

export default function WhatsAppButton() {
  const pathname = usePathname();
  const { whatsAppButtonEnabled } = usePublicSettings();
  // Devina's own internal tool, not a customer-facing surface -- a "chat with us" prompt has
  // no reason to float over the admin panel.
  if (pathname.startsWith("/admin")) return null;
  if (!whatsAppButtonEnabled) return null;

  const href = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(PREFILLED_MESSAGE)}`;
  return (
    // calc(...+env(safe-area-inset-*)) on top of the usual 24px -- on a notched/gesture-nav
    // phone (most current iPhones), a flat bottom-6/right-6 sits right at the edge of the
    // system's own reserved safe area, next to (or crowded by) the home-indicator strip. This
    // keeps the same 24px margin from whatever the *real* usable edge is on every device,
    // falling back to plain 24px on anything that doesn't define the safe-area variables at all.
    <div
      className="group fixed z-40"
      style={{
        bottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))",
        right: "calc(1.5rem + env(safe-area-inset-right, 0px))",
      }}
    >
      <span
        role="tooltip"
        className="pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 whitespace-nowrap rounded-lg bg-ink-dark px-3 py-1.5 text-sm font-medium text-white opacity-0 shadow-lg transition duration-200 group-hover:opacity-100"
      >
        Chat with us
      </span>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        className="flex size-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition hover:scale-105 hover:shadow-xl"
      >
        <svg viewBox="0 0 32 32" className="size-7" fill="currentColor" aria-hidden>
          <path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.258.594 4.472 1.72 6.416L3.2 28.8l6.56-1.68a12.74 12.74 0 0 0 6.244 1.68h.006c7.06 0 12.8-5.74 12.8-12.8s-5.74-12.8-12.806-12.8Zm0 23.36h-.005a10.53 10.53 0 0 1-5.37-1.47l-.385-.229-3.892.997.999-3.79-.251-.39a10.552 10.552 0 0 1-1.618-5.678c0-5.834 4.75-10.583 10.588-10.583 2.828 0 5.486 1.102 7.485 3.103a10.516 10.516 0 0 1 3.098 7.486c0 5.834-4.75 10.554-10.65 10.554Zm5.79-7.906c-.317-.159-1.877-.927-2.168-1.033-.29-.106-.502-.159-.713.16-.212.317-.82 1.032-1.005 1.244-.185.212-.37.238-.687.08-.317-.16-1.338-.494-2.549-1.575-.942-.84-1.578-1.878-1.763-2.195-.185-.318-.02-.49.139-.648.143-.142.317-.37.476-.556.16-.185.212-.318.318-.53.106-.212.053-.397-.026-.556-.08-.159-.713-1.72-.977-2.354-.257-.617-.518-.534-.713-.544l-.607-.011a1.166 1.166 0 0 0-.846.397c-.291.318-1.11 1.086-1.11 2.647s1.137 3.07 1.296 3.282c.16.212 2.24 3.42 5.428 4.795.758.328 1.35.523 1.812.669.762.242 1.455.208 2.003.126.611-.091 1.877-.767 2.141-1.508.265-.741.265-1.376.185-1.508-.08-.132-.291-.212-.608-.37Z" />
        </svg>
      </a>
    </div>
  );
}
